// @flow
import React from 'react';
import { IS_SPLITTING_OF_PL_AND_PNI_IN_ENTERPRISE_CLUSTER_CONFIG_LIVE } from '@streaming-projects/constants';
import { isExpandedInputRequiredForKafka } from '@src/common-utils/utils';
import { useEstimateRateCardId } from '@src/custom-hooks/useEstimateRateCardId';

import { DataGrid } from '../grid-utils/DataGrid';
import {
  CLUSTER_DEPLOYMENT,
  CLUSTER_ID,
  CLUSTER_ID_BACKEND_NAME,
  CLUSTER_NAME,
  CLUSTER_NAME_BACKEND_NAME,
  CLUSTER_NETWORKING,
  CLUSTER_NETWORKING_TYPE,
  CLUSTER_PARTITIONS,
  CLUSTER_PROVIDER,
  CLUSTER_REGION,
  CLUSTER_REGION_BACKEND_NAME,
  CLUSTER_RETENTION_BACKEND_NAME,
  CLUSTER_SLA_BACKEND_NAME,
  CLUSTER_SLA_DISPLAY_NAME,
  CLUSTER_TYPE,
  CLUSTER_TYPE_BACKEND_NAME,
  ESTIMATE_CLUSTER_CONFIGURATION_SUMMARY,
  IS_RTCE_LIVE_IN_CE1,
  NAME,
  TYPE,
} from '../../../constants';
import { ClusterNameRenderer } from '../clusters-summary/ClusterNameRenderer';
import {
  NumberRoundedRenderer,
  Number2DPRenderer,
  getConditionalNARendererAndValueGetter,
} from '../grid-utils/renderers';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { StyledH3 } from '../../presentational/headings/StyledH3';
import { DEFAULT_DEFAULT_COLUMN_DEFINITION } from '../grid-utils/constants';

const getThroughputColumns = (isForEnterpriseClusterPage) => {
  return isForEnterpriseClusterPage
    ? [
        {
          headerName: 'Avg Write Throughput (MB/s)',
          children: [
            {
              headerName: 'PNI',
              headerTooltip: 'PNI',
              field: 'Avg Write Throughput (MB/s) - PNI',
              backEndFieldName: 'average_write_pni',
              cellRenderer: Number2DPRenderer,
            },
            {
              headerName: 'PL',
              headerTooltip: 'Private Link',
              field: 'Avg Write Throughput (MB/s) - Private Link',
              backEndFieldName: 'average_write_pl',
              cellRenderer: Number2DPRenderer,
            },
          ],
        },
        {
          headerName: 'Avg Read Throughput (MB/s)',
          children: [
            {
              headerName: 'PNI',
              headerTooltip: 'PNI',
              field: 'Avg Read Throughput (MB/s) - PNI',
              backEndFieldName: 'average_read_pni',
              cellRenderer: Number2DPRenderer,
            },
            {
              headerName: 'PL',
              headerTooltip: 'Private Link',
              field: 'Avg Read Throughput (MB/s) - Private Link',
              backEndFieldName: 'average_read_pl',
              cellRenderer: Number2DPRenderer,
            },
          ],
        },
      ]
    : [
        {
          headerName: 'Avg Throughput (MB/s)',
          children: [
            {
              headerName: 'Write',
              headerTooltip: 'Write',
              field: 'Avg Write Throughput (MB/s)',
              backEndFieldName: 'average_write',
              cellRenderer: Number2DPRenderer,
            },
            {
              headerName: 'Read',
              headerTooltip: 'Read',
              field: 'Avg Read Throughput (MB/s)',
              backEndFieldName: 'average_read',
              cellRenderer: Number2DPRenderer,
            },
          ],
        },
      ];
};

const getRendererAndValueGetterForExpandedInputColumns = (rateCardId) => {
  return getConditionalNARendererAndValueGetter(
    NumberRoundedRenderer,
    (props) => !isExpandedInputRequiredForKafka(props?.data?.[TYPE], rateCardId)
  );
};

export const ClusterConfigurationsSummaryContainer = ({
  clusterConfigsArray,
  addLinkToClusterName = true,
  disabled = false,
  isForEnterpriseClusterPage = false,
}) => {
  const rateCardId = useEstimateRateCardId();
  const inputDataSource = clusterConfigsArray;
  const areAnyClustersConfigured = inputDataSource.length > 0;

  const columnDefs = [
    {
      field: CLUSTER_ID,
      backEndFieldName: CLUSTER_ID_BACKEND_NAME,
      hide: true,
    },
    {
      field: NAME,
      headerTooltip: CLUSTER_NAME,
      cellRenderer: addLinkToClusterName ? ClusterNameRenderer : null,
      backEndFieldName: CLUSTER_NAME_BACKEND_NAME,
    },
    {
      field: CLUSTER_PROVIDER,
      headerTooltip: CLUSTER_PROVIDER,
      backEndFieldName: 'cloud_provider',
    },
    {
      field: TYPE,
      headerTooltip: CLUSTER_TYPE,
      backEndFieldName: CLUSTER_TYPE_BACKEND_NAME,
    },
    {
      field: CLUSTER_DEPLOYMENT,
      headerTooltip: CLUSTER_DEPLOYMENT,
      backEndFieldName: 'deployment',
    },
    {
      field: CLUSTER_REGION,
      headerTooltip: CLUSTER_REGION,
      backEndFieldName: CLUSTER_REGION_BACKEND_NAME,
    },
    {
      field: CLUSTER_NETWORKING,
      headerTooltip: CLUSTER_NETWORKING_TYPE,
      backEndFieldName: 'networking',
    },
    {
      field: CLUSTER_SLA_DISPLAY_NAME,
      headerTooltip: CLUSTER_SLA_DISPLAY_NAME,
      backEndFieldName: CLUSTER_SLA_BACKEND_NAME,
    },
    ...getThroughputColumns(
      isForEnterpriseClusterPage && IS_SPLITTING_OF_PL_AND_PNI_IN_ENTERPRISE_CLUSTER_CONFIG_LIVE
    ),
    {
      field: 'Retention',
      headerTooltip: 'Retention in days',
      backEndFieldName: CLUSTER_RETENTION_BACKEND_NAME,
    },
    {
      headerName: 'Avg Storage (GB)',
      children: [
        {
          headerName: 'User Visible',
          headerTooltip: 'User Visible',
          field: 'Avg Storage (GB) - User Visible',
          backEndFieldName: 'average_storage_user_visible',
          cellRenderer: NumberRoundedRenderer,
        },
        {
          headerName: 'After Repl.',
          headerTooltip: 'After Replication',
          field: 'Avg Storage (GB) - After Replication',
          backEndFieldName: 'average_storage_after_repl',
          cellRenderer: NumberRoundedRenderer,
        },
        {
          headerName: 'Existing Storage',
          headerTooltip: 'Existing Storage (GB) - After Replication',
          field: 'Existing Storage (GB)',
          backEndFieldName: 'existing_storage',
        },
      ],
    },
    {
      field: CLUSTER_PARTITIONS,
      headerTooltip: CLUSTER_PARTITIONS,
      backEndFieldName: 'partitions',
      headerName: CLUSTER_PARTITIONS,
    },
    {
      field: 'CKUs',
      headerTooltip: 'CKUs',
      backEndFieldName: 'average_ckus',
      headerName: 'CKUs',
      cellRenderer: NumberRoundedRenderer,
    },
    {
      field: 'ECKUs',
      headerTooltip: 'ECKUs',
      backEndFieldName: 'average_eckus',
      headerName: 'ECKUs',
      cellRenderer: NumberRoundedRenderer,
    },
    {
      field: 'Avg SCC',
      headerTooltip: 'Avg. Simultaneously Connected Clients',
      backEndFieldName: 'average_simultaneously_connected_clients',
      ...getRendererAndValueGetterForExpandedInputColumns(rateCardId),
    },
    {
      field: 'Avg CAPS',
      headerTooltip: 'Avg. Connection Attempts Per Second',
      backEndFieldName: 'average_connection_attempts_per_sec',
      ...getRendererAndValueGetterForExpandedInputColumns(rateCardId),
    },
    {
      field: 'Avg RPS',
      headerTooltip: 'Avg. Requests Per Second',
      backEndFieldName: 'average_requests_per_sec',
      ...getRendererAndValueGetterForExpandedInputColumns(rateCardId),
    },
    {
      headerName: 'Tableflow',
      children: [
        {
          field: 'Avg Tableflow Topics',
          headerName: 'Topics',
          headerTooltip: 'Avg. Tableflow Topics',
          backEndFieldName: 'average_tableflow_topics',
          cellRenderer: NumberRoundedRenderer,
        },
        {
          field: 'Avg Tableflow Throughput',
          headerName: 'Throughput',
          headerTooltip: 'Avg. Tableflow Throughput (MB/s)',
          backEndFieldName: 'average_tableflow_throughput',
          cellRenderer: Number2DPRenderer,
        },
      ],
    },
    ...(IS_RTCE_LIVE_IN_CE1
      ? [
          {
            headerName: 'RTCE',
            children: [
              {
                field: 'Avg RTCE CCUs',
                headerName: 'CCUs',
                headerTooltip: 'Avg. RTCE CCUs',
                backEndFieldName: 'average_rtce_ccus',
                cellRenderer: NumberRoundedRenderer,
              },
              {
                field: 'Avg RTCE Storage',
                headerName: 'Storage',
                headerTooltip: 'Avg. RTCE Storage (GB)',
                backEndFieldName: 'average_rtce_storage',
                cellRenderer: Number2DPRenderer,
              },
            ],
          },
        ]
      : []),
    {
      headerName: 'Connect',
      children: [
        {
          field: 'Connector Tasks',
          headerName: 'Tasks',
          headerTooltip: 'Tasks',
          backEndFieldName: 'average_connect_task',
          cellRenderer: NumberRoundedRenderer,
        },
        {
          field: 'Connector Throughput',
          headerName: 'Throughput',
          headerTooltip: 'Avg Throughput in (MB/s)',
          backEndFieldName: 'average_connect_throughput',
          cellRenderer: Number2DPRenderer,
        },
      ],
    },
    {
      field: 'CSUs',
      headerTooltip: 'KSQL CSUs',
      backEndFieldName: 'average_ksql_csus',
      headerName: 'CSUs',
      cellRenderer: NumberRoundedRenderer,
    },
    {
      field: 'Links',
      headerTooltip: 'Cluster Links',
      backEndFieldName: 'average_cluster_links',
      cellRenderer: NumberRoundedRenderer,
    },
  ];

  const defaultColDef = {
    ...DEFAULT_DEFAULT_COLUMN_DEFINITION,
    wrapHeaderText: true,
  };

  return (
    <PaddedAndRaisedSegment disabled={disabled}>
      {!areAnyClustersConfigured ? (
        <StyledH3 as="h3" text="Add a cluster to see results" />
      ) : (
        <DataGrid
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          displayResizingColsOption={true}
          downloadAllCols={false}
          inputDataSource={inputDataSource}
          label={ESTIMATE_CLUSTER_CONFIGURATION_SUMMARY}
          tooltipShowDelay={50}
        />
      )}
    </PaddedAndRaisedSegment>
  );
};
